import { useAuthStore } from '../store/useAuthStore';

interface Envelope<T> {
  sessionState: string;
  success: boolean;
  data: T | null;
  messages: Array<{ severity: string; message: string; details?: Record<string, unknown> }>;
}

export interface PatchMeBody {
  pip_default_on?: boolean;
  mood_tracking_enabled?: boolean;
  preferred_background_mode?: 'off' | 'blur' | 'virtual';
  preferred_background_id?: number | null;
  safety_checkin_enabled?: boolean;
}

export interface PatchMeResponse {
  user_id: string;
  pip_default_on: boolean;
  mood_tracking_enabled?: boolean;
  preferred_background_mode?: 'off' | 'blur' | 'virtual';
  preferred_background_id?: number | null;
  safety_checkin_enabled?: boolean;
}

export class MeApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * PATCH /api/v1/auth/me with an allow-listed partial body.
 * CSRF header attached automatically from useAuthStore.csrfToken.
 */
export async function patchMe(body: PatchMeBody): Promise<PatchMeResponse> {
  const csrf = useAuthStore.getState().csrfToken;
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (csrf) headers.set('X-CSRF-Token', csrf);

  const res = await fetch('/api/v1/auth/me', {
    method: 'PATCH',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const envelope = (await res.json()) as Envelope<PatchMeResponse>;
  if (!res.ok || !envelope.success || envelope.data === null) {
    const msg = envelope.messages[0];
    const code = (msg?.details?.code as string | undefined) ?? 'unknown_error';
    throw new MeApiError(res.status, code, msg?.message ?? 'PATCH /me failed');
  }
  return envelope.data;
}
